import React, {
  useCallback, useEffect, useMemo, useState, ReactElement, Fragment,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import MobileAppLink from '../../../../components/mobile-app-link/mobile-app-link.component';
import Grid from '../../../../components/grid/grid.component';
import GridItem from '../../../../components/grid-item/grid-item.component';
import StatusBar from '../../../../components/status-bar/status-bar.component';
import TextHeader from '../../../../components/text-header/text-header.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import Loader from '../../../../components/loader/loader.component';
import Button from '../../../../components/button/button.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import Form from '../../../../components/form/form.component';
import FormContainer from '../../../../components/form-container/form-container.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldPassword from '../../../../components/form-field-password/form-field-password.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import OktaButton from '../../../../components/okta-button/okta-button.component';
import OktaAccountModal from '../../../../components/okta-account-modal/okta-account-modal.component';
import LoginValidator from './login.validator';
import MainService from '../../../../services/main-service';
import ScreenService from '../../../../services/screen-service';
import FormatService from '../../../../services/format-service';
import LocalStorageService from '../../../../services/local-storage-service';
import OktaService from '../../../../services/okta-service';
import FormService from '../../../../services/form-service';
import useScreenMobile from '../../../../hooks/use-screen-mobile';
import useFormStructureModified from '../../../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../../../hooks/use-server-form-errors';
import { getAccountOktaData, loginUser, resetActionHash } from './login.action';
import { resetPasswordRecovered } from '../password-recovery/password-recovery.action';
import { resetFormErrors } from '../../app.action';
import {
  ROUTE_FORGOT_PASSWORD, ROUTE_DASHBOARD, ROUTE_VERIFICATION, ROUTE_PASSWORD_RECOVERY, ROUTE_LOGIN,
  ROUTE_VERIFICATION_SETUP,
} from '../../../../constants/system/routes';
import { FORM_ID_LOGIN } from '../../../../constants/system/form-ids';
import { QUERY_PARAM_OKTA_ACCOUNT } from '../../../../constants/system/query-params';
import {
  ACTION_TYPE_PASSWORD_RECOVERY, ACTION_TYPE_VERIFICATION, ACTION_TYPE_VERIFICATION_SETUP,
} from '../../../../constants/entity/action-types';
import { NODE_ENV_PRODUCTION } from '../../../../constants/system/node-envs';

const Login: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isPasswordRecovered,
    currentUser,
    actionHash,
    actionType,
    homepageRoute,
    oktaAccountData,
    oktaAuth,
    authState: oktaAuthState,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const isMobileScreen: boolean = useScreenMobile();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, true);
  const dispatch: ReduxDispatch = useDispatch();
  const location: ReactRouterLocation = useLocation();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryOktaAccount: string | null = locationQueryParams && Object.keys(locationQueryParams).length
  && locationQueryParams[QUERY_PARAM_OKTA_ACCOUNT]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_OKTA_ACCOUNT].toString()) : null;
  const isSsoEnabled: boolean = MainService.NODE_ENV === NODE_ENV_PRODUCTION && !MainService.DISABLE_SSO;
  const oktaAuthToken: string | null = (oktaAuthState && oktaAuthState.accessToken
    && oktaAuthState.accessToken.accessToken) || null;
  const isOktaAuthenticated: boolean = (oktaAuthState && oktaAuthState.isAuthenticated) || false;
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;

  const ACTION_TYPE_ROUTE_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [ACTION_TYPE_VERIFICATION]: ROUTE_VERIFICATION,
    [ACTION_TYPE_PASSWORD_RECOVERY]: ROUTE_PASSWORD_RECOVERY,
    [ACTION_TYPE_VERIFICATION_SETUP]: ROUTE_VERIFICATION_SETUP,
  }), []);

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [isPasswordVisible, setIsPasswordVisible]: [boolean, Function] = useState(false);
  const [isOktaAccountModalOpen, setIsOktaAccountModalOpen]: [boolean, Function] = useState(false);
  const [oktaAccountModalErrorMessage, setOktaAccountModalErrorMessage]: [string | null, Function] = useState(null);

  const isSaveDisabled: boolean = isFormSaving || (isFormPristine && !isFormStructureModified);
  const actionTypeRoute: string = actionType && ACTION_TYPE_ROUTE_MAP[actionType];
  const storedOktaAccountName: string | null = LocalStorageService.getOktaAccountName();
  const storedOktaAccountClientId: string | null = LocalStorageService.getOktaAccountClientId();
  const isAuthLoading: boolean = isOktaAuthenticated && !currentUser && !formError && !errorMessage;

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    setErrorMessage(null);

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(loginUser({
        loginForm: {
          user: {
            email: values.email,
            password: values.password,
          },
          keepLoggedIn: values.keepLoggedIn,
        },
      }, (response: ActionResponse): void => {
        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, []);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [withFormErrors, formValues]);

  const openOktaAccountModal: Function = useCallback((): void => {
    setIsOktaAccountModalOpen(true);
  }, []);

  const closeOktaAccountModal: Function = useCallback((): void => {
    setIsOktaAccountModalOpen(false);
    setOktaAccountModalErrorMessage(null);
  }, []);

  const onApplyOktaAccountNameClick: Function = useCallback((oktaAccountName: string): void => {
    dispatch(getAccountOktaData({
      oktaAccountName,
    }, (): void => {
      setIsOktaAccountModalOpen(false);
      setOktaAccountModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setOktaAccountModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const oktaLogin: Function = useCallback((): void => {
    oktaAuth.signInWithRedirect();
  }, [oktaAuth]);

  const oktaLoginClick: MouseEventHandlerFunction = useCallback((): void => {
    if (storedOktaAccountName && storedOktaAccountClientId) {
      oktaLogin();
    } else {
      openOktaAccountModal();
    }

    setErrorMessage(null);
  }, [oktaAuth, storedOktaAccountName, storedOktaAccountClientId]);

  const changeOktaAccountClick: MouseEventHandlerFunction = useCallback((): void => {
    if (storedOktaAccountName && storedOktaAccountClientId) {
      openOktaAccountModal();
    }
  }, [storedOktaAccountName, storedOktaAccountClientId]);

  const onPasswordVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordVisible(value);
  }, []);

  const onPasswordRecovered: Function = useCallback((): void => {
    setStatusMessage('Your password was changed successfully');
    setIsOpenMessage(true);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (isPasswordRecovered) {
      onPasswordRecovered();
      dispatch(resetPasswordRecovered());
    }
  }, [isPasswordRecovered]);

  useEffect((): void => {
    if (actionHash) {
      dispatch(resetActionHash());
    }
  }, [actionHash]);

  useEffect((): void => {
    if (oktaAuthToken && isOktaAuthenticated) {
      dispatch(loginUser({
        loginForm: {
          authToken: oktaAuthToken,
        },
      }, undefined, (exception: ServerError): void => {
        if (exception && exception.message) {
          setErrorMessage(exception.message);
          submitForm();
        }
      }));
    }
  }, [oktaAuthToken]);

  useEffect((): void => {
    if (oktaAccountData && (!storedOktaAccountName || !storedOktaAccountClientId
      || storedOktaAccountName !== oktaAccountData.oktaAccountName
      || storedOktaAccountClientId !== oktaAccountData.oktaAccountClientId)) {
      LocalStorageService.setOktaAccountName(oktaAccountData.oktaAccountName);
      LocalStorageService.setOktaAccountClientId(oktaAccountData.oktaAccountClientId);

      const queryParams: LocationQueryParams = {
        ...(locationQueryParams || {}),
        [QUERY_PARAM_OKTA_ACCOUNT]: oktaAccountData.oktaAccountName,
      };

      ScreenService.openLink(`${ROUTE_LOGIN}${FormatService.formatQueryParams(queryParams)}`);
    }
  }, [oktaAccountData]);

  useEffect((): void => {
    if (queryOktaAccount) {
      if (storedOktaAccountName && storedOktaAccountClientId) {
        oktaLogin();
      } else {
        dispatch(getAccountOktaData({
          oktaAccountName: queryOktaAccount,
        }, undefined, (exception: ServerError): void => {
          if (exception && exception.message) {
            setErrorMessage(exception.message);
          }
        }));
      }
    }
  }, [queryOktaAccount]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (currentUser && currentUser.id) {
    return <Navigate to={homepageRoute || ROUTE_DASHBOARD} />;
  }

  if (actionHash && actionTypeRoute) {
    return <Navigate to={`${actionTypeRoute}/${actionHash}`} />;
  }

  return (
    <Fragment>
      <Form withAutocomplete id={FORM_ID_LOGIN} onChange={onFormChange}>
        <FlexContainer isCentered>
          {
            !isAuthLoading ? (
              <PaddedFlexContainer isLarge isColumn isTextCenter width={900}>
                <Grid withLargeSpacing>
                  <GridItem isFullWidth>
                    <TextHeader>
                      Welcome Back
                    </TextHeader>
                    <TextBlock>
                      Please sign in below
                    </TextBlock>
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldText isOutlined withAutocompleteUserName name="email" label="Email" />
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldPassword
                      isOutlined
                      withAutocompleteCurrentPassword
                      isVisible={isPasswordVisible}
                      name="password"
                      label="Password"
                      onVisibilityChange={onPasswordVisibilityChange}
                    />
                  </GridItem>
                  <GridItem isFullWidth>
                    <Button
                      isActive
                      isDefault
                      isExtended
                      isDisabled={isSaveDisabled}
                      onClick={onSubmitForm}
                    >
                      Sign In
                    </Button>
                  </GridItem>
                  <GridItem isFullWidth>
                    <FormFieldCheckbox
                      name="keepLoggedIn"
                      label={(
                        <TextBlock>
                          Remember this device
                        </TextBlock>
                      )}
                    />
                  </GridItem>
                  <GridItem isFullWidth>
                    <FilteredLink link={ROUTE_FORGOT_PASSWORD}>
                      Forgot Password
                    </FilteredLink>
                  </GridItem>
                  {
                    isSsoEnabled && (
                      <GridItem isFullWidth>
                        <OktaButton onClick={oktaLoginClick} />
                      </GridItem>
                    )
                  }
                  {
                    isSsoEnabled && storedOktaAccountName && storedOktaAccountClientId && (
                      <GridItem isFullWidth>
                        <Button isActive isText isExtended onClick={changeOktaAccountClick}>
                          Change Okta account
                        </Button>
                      </GridItem>
                    )
                  }
                </Grid>
                <MobileAppLink />
                {
                  (!!errorMessage || !!formError || !!mainServerFormError) && !isFormSaving && (
                    <ErrorBlock errorMessage={errorMessage || formError || mainServerFormError} />
                  )
                }
              </PaddedFlexContainer>
            ) : (
              <Loader isPageLoader />
            )
          }
        </FlexContainer>
      </Form>
      <OktaAccountModal
        isOpen={isOktaAccountModalOpen}
        errorMessage={oktaAccountModalErrorMessage}
        onApplyOktaAccountNameClick={onApplyOktaAccountNameClick}
        onCloseModal={closeOktaAccountModal}
      />
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    passwordRecovery,
    login,
  } = reducers;

  initialValues.email = null;
  initialValues.password = null;

  return {
    currentUser: app.baseData && app.baseData.currentUser,
    isPasswordRecovered: passwordRecovery.isPasswordRecovered,
    actionHash: login.actionHash,
    actionType: login.actionType,
    homepageRoute: login.homepageRoute,
    oktaAccountData: login.oktaAccountData,
  };
};

const LoginForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ login }: Reducers): ValidationErrorsMap => (
    login.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ login }: Reducers): ValidationErrorsMap => (
    login.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_LOGIN}
      component={Login}
      validator={LoginValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default OktaService.withOktaAuth(connect(mapState)(LoginForm) as ReactComponent);
